import { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Button } from '~/modules/elements/akui/button';
import useKeyboardNav from '~/modules/hooks/use-keyboard-nav';
import { useSongQueue } from '~/modules/song-queue/hooks';
import SongQueue from '~/modules/song-queue/song-queue';
import { ordinal } from '~/modules/utils/ordinal';
import { formatQueueDuration } from '~/routes/sing-a-song/song-selection/components/queue/format-duration';
import QueueCard from '~/routes/sing-a-song/song-selection/components/queue/queue-card';
import {
  firstActionOf,
  isActionOf,
  landingSpot,
  openPosition,
  spotAfterRemoval,
} from '~/routes/sing-a-song/song-selection/components/queue/queue-focus';

interface Props {
  // The dock holds the keyboard: the running order is being rearranged rather than the list browsed.
  active: boolean;
  onExit: () => void;
  // The expanded preview is open over the list, so the dock is the 64px peek behind the card.
  collapsed: boolean;
  // Reports how much of the bottom edge the dock is taking, so the list can end above it.
  onHeightChange: (height: number) => void;
}

// The party's running order along the bottom edge of the song list. A queue is a sequence, so it reads left to right,
// and the move and remove actions belong to the focused card rather than sitting on every row at once - four songs are
// four stops on the phone instead of twelve. While the expanded preview is open it drops to a 64px peek behind the card
// (`F3c`), which is what confirms a song just queued from there landed, and where in the order it landed.
export default function QueueDock({ active, onExit, collapsed, onHeightChange }: Props) {
  const queue = useSongQueue();
  // State rather than a ref: the height observer below has to run once the node exists, and a ref
  // being filled in is not a render.
  const [dock, setDock] = useState<HTMLDivElement | null>(null);
  const [justQueuedId, setJustQueuedId] = useState<string | null>(null);
  const queuedIds = useRef(new Set(queue.map((item) => item.song.id)));

  // Two rows, the way the toolbar above the list is two rows: left and right move along whichever
  // row has the keyboard, and up and down change rows. Grid navigation reads the boxes on screen,
  // and Clear sits level with the cards - so down out of a card would land on it rather than on the
  // card's own actions underneath.
  const [row, setRow] = useState<'cards' | 'actions'>('cards');
  const inDock = active && !collapsed;

  const cards = useKeyboardNav({
    enabled: inDock && row === 'cards',
    direction: 'horizontal',
    onBackspace: onExit,
    backspaceHelp: 'Back to the song list',
    title: 'Queue',
  });
  // Which card the action row belongs to, read before the row's own nav so its title can name it.
  const open = openPosition(cards.focused);
  const openItem = open > 0 ? queue[open - 1] : undefined;
  const actions = useKeyboardNav({
    enabled: inDock && row === 'actions',
    direction: 'horizontal',
    onBackspace: () => setRow('cards'),
    backspaceHelp: 'Back to the queue',
    // The phone swaps the whole card list for these three buttons, so without the card named here
    // they arrive as "Move earlier / Move later / Remove" of nothing in particular.
    title: openItem ? `Queue \u00b7 ${open} \u00b7 ${openItem.song.title}` : 'Queue',
  });
  // Where the selection has to be put once the running order has moved under it. Applied from an
  // effect because the name only exists after the card it belongs to has re-rendered.
  const [pending, setPending] = useState<string | null>(null);

  useHotkeys('down', () => setRow('actions'), { enabled: inDock && row === 'cards' && open > 0 }, [inDock, row, open]);
  useHotkeys('up', () => setRow('cards'), { enabled: inDock && row === 'actions' }, [inDock, row]);
  // Up out of the card row leaves the dock: the list is the zone above it.
  useHotkeys('up', onExit, { enabled: inDock && row === 'cards' }, [inDock, row, onExit]);

  useEffect(() => {
    if (!pending) return;

    if (isActionOf(pending)) actions.focusElement(pending);
    else cards.focusElement(pending);
    setPending(null);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- runs on the render that registered the name; both `focusElement`s are fresh each render by design
  }, [pending, queue]);

  // A card removed from the phone, or the last one taken by the song that just started, leaves
  // nothing to hold the keyboard - hand it back rather than trapping it in an empty dock. Keyed on
  // the running order emptying rather than on it being empty, so a dock that never had one to show
  // does not claim to be handing anything back.
  const hadQueue = useRef(queue.length > 0);
  useEffect(() => {
    const emptied = hadQueue.current && queue.length === 0;
    hadQueue.current = queue.length > 0;
    if (active && emptied) onExit();
  }, [active, queue.length, onExit]);

  // Re-entering starts at the head of the queue on the card row: the selection is left wherever the
  // last visit ended, and a dock that reopens on "Remove" is not what anyone asked for.
  useEffect(() => {
    if (!active) return;

    setRow('cards');
    setPending('queue-item-1');
  }, [active]);

  // Only an add made while the card is open counts: the peek is that add's confirmation, so opening
  // a preview over a running order that was already there must not announce one of its songs.
  const [wasCollapsed, setWasCollapsed] = useState(collapsed);
  if (wasCollapsed !== collapsed) {
    setWasCollapsed(collapsed);
    setJustQueuedId(null);
  }

  useEffect(() => {
    const added = queue.find((item) => !queuedIds.current.has(item.song.id));
    queuedIds.current = new Set(queue.map((item) => item.song.id));
    if (added && collapsed) setJustQueuedId(added.song.id);
  }, [queue, collapsed]);

  useEffect(() => {
    if (!dock) return;

    // Reported straight away as well as on resize: the observer's first callback does not arrive
    // until a later frame, and until it does the list has no room reserved and the dock sits over
    // its last row.
    onHeightChange(dock.offsetHeight);
    const observer = new ResizeObserver(() => onHeightChange(dock.offsetHeight));
    observer.observe(dock);

    return () => {
      observer.disconnect();
      onHeightChange(0);
    };
  }, [dock, onHeightChange]);

  // Moves a card and takes the selection with it, so one press of Enter after another walks a song along the running
  // order instead of swapping it back and forth with its neighbour.
  const moveCard = (index: number, direction: -1 | 1) => {
    const spot = landingSpot(index, direction, queue.length);

    SongQueue.move(queue[index].song.id, direction);
    cards.focusElement(`queue-item-${index + 1 + direction}`);
    if (isActionOf(spot)) setPending(spot);
    // The move that got it here is spent at either end of the queue, so its button is now disabled
    // and the card itself takes the keyboard back.
    else setRow('cards');
  };

  if (queue.length === 0) return null;

  const duration = formatQueueDuration(SongQueue.getTotalDuration());
  const justQueuedPosition = queue.findIndex((item) => item.song.id === justQueuedId) + 1;

  if (collapsed) {
    return (
      <div
        // Between the preview's backdrop (z-200) and the card itself (z-202): F10's whole point is
        // that the queue survives the preview opening over it, so "behind the card" has to mean
        // visible underneath it and not lost under the dim. Clear of the keyboard hint strip along
        // the bottom, which is fixed there at z-1000 while the card has control. One slide in,
        // nothing that repeats.
        className="motion-preset-slide-up motion-duration-300 typography pointer-events-auto fixed bottom-[3.5rem] left-0 z-201 flex h-16 w-full items-center gap-4.5 border-t border-white/15 bg-slate-950/96 px-10"
        data-test="queue-dock"
        data-collapsed={true}>
        <span className="text-[1.125rem] font-bold text-white/85">
          <span className="text-active text-lg">{queue.length}</span> queued · {duration}
        </span>
        <div className="ml-auto flex items-center gap-2.5">
          {queue.map((item) => (
            <div
              key={item.song.id}
              data-test="queue-peek-thumbnail"
              data-song={item.song.id}
              className={`h-11 w-20 rounded-md bg-[#2b2b2b] bg-cover bg-center ${
                item.song.id === justQueuedId ? 'shadow-[inset_0_0_0_2px_rgb(255,165,0)]' : ''
              }`}
              style={{ backgroundImage: `url('https://i3.ytimg.com/vi/${item.song.video}/hqdefault.jpg')` }}
            />
          ))}
          {justQueuedPosition > 0 && (
            <span className="ph-no-capture ml-2 text-[0.9375rem] text-white/50" data-test="queue-peek-added">
              {queue[justQueuedPosition - 1].song.title} added {ordinal(justQueuedPosition)}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setDock}
      className="typography pointer-events-auto fixed bottom-0 left-0 z-102 flex w-full items-start gap-8 border-t border-white/15 bg-slate-950/92 px-11 pt-5 pb-6 shadow-[0_-20px_60px_rgba(0,0,0,.5)]"
      data-test="queue-dock"
      data-collapsed={false}
      data-active={active}>
      <div className="flex w-[15.625rem] shrink-0 flex-col gap-1.5 pt-2">
        <div className="flex items-baseline gap-2.5">
          <span className="text-active stroke-text text-[2.625rem] leading-none font-bold">{queue.length}</span>
          <span className="text-[1.375rem] font-bold">{queue.length === 1 ? 'song queued' : 'songs queued'}</span>
        </div>
        {/* The queue does not advance the moment a song ends - the post-game screens come first, and
            the Next button there is what announces it. */}
        <span className="text-[1.125rem] text-white/50">{duration} · the next one starts after the results</span>
        <Button
          size="mini"
          className="mt-2 h-11! w-[11.875rem] text-[1.125rem]!"
          data-test="queue-clear"
          {...cards.register('queue-clear', () => SongQueue.clear(), 'Clear the queue', false, {
            control: { type: 'button', label: 'Clear the queue', icon: null },
          })}>
          Clear the queue
        </Button>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <span className="text-[1.0625rem] tracking-[0.08em] text-white/45 uppercase">Up next</span>
          <span className="text-[1rem] text-white/35">◀ ▶ moves along the queue</span>
        </div>
        {/* A running order longer than the dock is wide keeps going past the right edge, so the lane
            scrolls with the selection rather than clipping the rest of it out of reach. */}
        <div className="relative min-w-0">
          <div className="flex [scrollbar-width:none] gap-3.5 overflow-x-auto [&::-webkit-scrollbar]:hidden">
            {queue.map((item, index) => (
              <QueueCard
                key={item.song.id}
                item={item}
                position={index + 1}
                registerCard={cards.register}
                registerAction={actions.register}
                open={open === index + 1}
                firstAction={firstActionOf(index, queue.length)}
                isFirst={index === 0}
                isLast={index === queue.length - 1}
                onOpen={() => setRow('actions')}
                onMoveEarlier={() => moveCard(index, -1)}
                onMoveLater={() => moveCard(index, 1)}
                onRemove={() => {
                  SongQueue.remove(item.song.id);
                  setRow('cards');
                  cards.focusElement(spotAfterRemoval(index, queue.length));
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
